import Cookies from 'js-cookie';
import { SlidersHorizontal, X } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import ExperimentBucketing from '|/app/developer-control-panel/experiment-bucketing';
import FeatureBranches from '|/app/developer-control-panel/feature-branches';
import MfeDevOverride from '|/app/developer-control-panel/mfe-dev-override';
import {
  MFE_FEATURE_BRANCH_COOKIE,
  MFE_DEV_OVERRIDE_COOKIE,
  OPD_DEV_OVERRIDE_COOKIE,
  OPD_FEATURE_BRANCH_COOKIE
} from '|/hooks/use-global-mfe-config';
import styles from './control-panel.module.css';

const OPEN_STORAGE_KEY = 'cvna-devpanel-open';

const isProduction = process.env.NODE_ENV === 'production';

const readOpenState = (): boolean => {
  if (isProduction) return false;
  try {
    return localStorage.getItem(OPEN_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const persistOpenState = (open: boolean): void => {
  try {
    localStorage.setItem(OPEN_STORAGE_KEY, String(open));
  } catch {
    // best-effort — storage may be blocked in sandboxed/webview contexts
  }
};

type IndicatorState = 'override' | null;

const getIndicatorState = (): IndicatorState => {
  const hasOverride = Cookies.get('cvna-feature-enable') === 'true';

  // VerifX Verifications MFE Override Cookies
  const verifsMfeLocalOverrideEnabled = Cookies.get(MFE_DEV_OVERRIDE_COOKIE) === 'true';
  const hasVerifxMfeBranchOverride = Cookies.get(MFE_FEATURE_BRANCH_COOKIE) !== undefined;
  const hasVerifxMfeOverride = verifsMfeLocalOverrideEnabled || hasVerifxMfeBranchOverride;

  // Verifx OPD MFE Override Cookies
  const opdMfeLocalOverrideEnabled = Cookies.get(OPD_DEV_OVERRIDE_COOKIE) === 'true';
  const hasOpdMfeBranchOverride = Cookies.get(OPD_FEATURE_BRANCH_COOKIE) !== undefined;
  const hasOpdMfeOverride = opdMfeLocalOverrideEnabled || hasOpdMfeBranchOverride;

  if (hasOverride || hasVerifxMfeOverride || hasOpdMfeOverride) return 'override';

  return null;
};

const DeveloperControlPanel = () => {
  const [isOpen, setIsOpen] = useState(readOpenState);

  const toggleOpen = useCallback(() => {
    setIsOpen(current => {
      const next = !current;
      persistOpenState(next);
      return next;
    });
  }, []);

  if (isProduction) return null;

  if (!isOpen) {
    const indicator = getIndicatorState();
    return (
      <button
        className={styles.launcher}
        onClick={toggleOpen}
        aria-label="Open Purchase dev panel"
        data-analytics-id="pp_devpanel_launcher"
      >
        <SlidersHorizontal size={22} aria-hidden />
        {indicator && <span className={styles.indicatorDot} />}
      </button>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.title}>Purchase Dev Panel</span>
        <button
          className={styles.closeBtn}
          onClick={toggleOpen}
          aria-label="Close Purchase dev panel"
          data-analytics-id="pp_devpanel_close"
        >
          <X size={16} aria-hidden />
        </button>
      </div>
      <div className={styles.body}>
        <ExperimentBucketing />
        <FeatureBranches />
        <MfeDevOverride />
      </div>
    </div>
  );
};

export default DeveloperControlPanel;
